import { Close } from "@mui/icons-material"
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography, Select, MenuItem, FormControl, InputLabel, DialogContentText, Switch } from "@mui/material";
import { useForm } from "react-hook-form"
import withShowAlert from "components/withShowAlert"
import { useCallback, useEffect, useState } from "react"
import MercadappRemoteAutocomplete from "components/MercadappRemoteAutocomplete"
import { index, markets } from "api/brandsApi"
import { LoadingButton } from "@mui/lab"
import { create, update } from "api/ioCredentialsApi"
import { LINX_IO_URL, MAKERTEC_IO_URL } from "constants"

function FormDialog({ open, setOpen, editingModel, showAlert }) {

  const [brand, setBrand] = useState({ id: editingModel?.brand_id, name: editingModel?.brand_name })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [brandMarkets, setBrandMarkets] = useState([])
  const [selectedMarket, setSelectedMarket] = useState({ id: editingModel?.market_id, name: editingModel?.market_name })
  const [showMarketDialog, setShowMarketDialog] = useState(false);
  const [isMarketSelected, setIsMarketSelected] = useState(false); 
  const [isEnabled, setIsEnabled] = useState(editingModel?.is_enabled || false);
  const [ioUrl, setIoUrl] = useState(editingModel?.io_url || LINX_IO_URL);

  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  const handleClose = (event, reason) => {
    if (reason && reason === "backdropClick") {
      return
    }

    setOpen(false)
  }

  const getErrorMessage = (field) => {
    if (errors[field]) {
      return errors[field].message
    }
    return undefined
  }

  const submitForm = async (data) => {
    setIsSubmitting(true)

    const body = {
      app_id: data.app_id,
      brand_id: brand.id,
      brand_name: brand.name,
      market_id: selectedMarket.id || null,
      market_name: selectedMarket.name || null,
      mercadapp_app_id: data.mercadapp_app_id,
      username: data.username,
      io_url: ioUrl 
    };

    if (data.password && data.password !== editingModel?.password) {
      body.password = data.password;
    }

    if (editingModel?.id) {
      body.is_enabled = isEnabled;
    }

    if (!editingModel?.id) {
      body.enabled_import_topics = [
        'product',
        'price',
        'stock',
        'order',
        'customer',
        'ack-request',
      ]
    }

    try {
      editingModel?.id ? await update(editingModel.id, body) : await create(body)
      const message = editingModel?.id ? 'Atualizado com sucesso.' : 'Criado com sucesso.'
      setOpen(false, true, message)
    } catch (error) {
      const errorData = error.response?.data?.message?.join("\n") || "Erro desconhecido, entre em contato com o suporte."
      showAlert(errorData, 'error')
    }

    setIsSubmitting(false)
  }

  const getBrands = async (search) => {
    const response = await index(search)
    return response.data
  }

  const fetchMarkets = async () => {
    try {
      const { data } = await markets(brand.id);
      setBrandMarkets(data);
    } catch (error) {
      setBrandMarkets([]);
    }
  };

  useEffect(() => {
    if (brand?.id) {
      fetchMarkets();
    }
  }, [brand]);

  const handleBrandChange = useCallback(
    (selected) => {
      setBrand(selected);
      setSelectedMarket({});
      setBrandMarkets([])
      setIsMarketSelected(false)
      setShowMarketDialog(false);
    },
    [brand]
  );

  const handleMarketChange = useCallback(({ target }) => {
    const selectedMarketId = target.value;
    setSelectedMarket(brandMarkets.find((market) => market.id === selectedMarketId));
    if (!isMarketSelected) {
      setIsMarketSelected(true);
      setShowMarketDialog(true);
    }
  }, [isMarketSelected, brandMarkets]);

  const handleSwitchChange = ({ target }) => setIsEnabled(target.checked);

  const getDisplayedPassword = (password) => {
    if (!password) return undefined;
    if (password.length <= 4) return password;
    const maskedPart = "*".repeat(password.length - 4);
    const lastFourChars = password.slice(-4);

    return maskedPart + lastFourChars;
  };

  return <Dialog
    fullWidth={true}
    maxWidth="lg"
    open={open}
    onClose={handleClose}>

    <DialogTitle mb={2}>
      <Typography variant={"h4"}>
        {editingModel?.id == null ? "Nova credencial" : 'Editar credencial'}
      </Typography>
      <IconButton
        aria-label="close"
        onClick={() => setOpen(false)}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <Close />
      </IconButton>
    </DialogTitle>

    <DialogContent>
        <form id="offer-form" onSubmit={handleSubmit(submitForm)}>
          <Grid container mt={1} spacing={2}>
              <Grid item xs={6} mb={1}>
              <TextField
                {...register("app_id")}
                error={getErrorMessage("app_id")}
                helperText={getErrorMessage("app_id")}
                sx={{ maxWidth: "none", width: "100%" }}
                id="app_id"
                name="app_id"
                label="App Id do linx IO"
                defaultValue={editingModel?.app_id}
                variant="standard"
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
              <Grid item xs={6} mb={1}>
                <TextField
                  {...register("mercadapp_app_id")}
                  error={getErrorMessage("mercadapp_app_id")}
                  helperText={getErrorMessage("mercadapp_app_id")}
                  sx={{ maxWidth: "none", width: "100%" }}
                  id="mercadapp_app_id"
                  name="mercadapp_app_id"
                  label="App Id da Mercadapp"
                  defaultValue={editingModel?.mercadapp_app_id}
                variant="standard"
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={6}>
              <MercadappRemoteAutocomplete
                noOptionsText={"Pesquise a marca"}
                loadOptionsFunction={getBrands}
                label="Marca das credenciais"
                name="brand"
                onChange={handleBrandChange}
                value={brand}
                getOptionLabel={(option) => {
                  return option?.name || ''
                }}
                isOptionEqualToValue={(option, value) => {
                  return option.id === value.id
                }}
              />
            </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="market-label" shrink={true}>Loja</InputLabel>
                  <Select
                    disabled={brandMarkets.length === 0}
                    variant="standard"
                    labelId="market-label"
                    id="market-select"
                    value={selectedMarket?.id}
                    onChange={handleMarketChange}
                    label="Select Market"
                  >
                    {brandMarkets.map((market) => (
                      <MenuItem key={market.id} value={market.id}>
                        {market.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register("username")}
                  error={getErrorMessage("username")}
                  helperText={getErrorMessage("username")}
                  sx={{ maxWidth: "none", width: "100%" }}
                  id="username"
                  name="username"
                  label="Usuário"
                  defaultValue={editingModel?.username}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register("password")}
                  error={getErrorMessage("password")}
                  helperText={getErrorMessage("password")}
                  sx={{ maxWidth: "none", width: "100%" }}
                  id="password"
                  name="password"
                  label="Senha"
                  placeholder={getDisplayedPassword(editingModel?.password)}
                  type={editingModel?.id ? "password" : "text" }
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="standard">
                  <InputLabel shrink>Servidor IO</InputLabel>
                  <Select
                    value={ioUrl}
                    onChange={(e) => setIoUrl(e.target.value)}
                    label="Servidor IO"
                  >
                    <MenuItem value={LINX_IO_URL}>Linx IO (api.linxio.net)</MenuItem>
                    <MenuItem value={MAKERTEC_IO_URL}>Makertec (api.io.makertec.com.br)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {editingModel?.id ? (
                <Grid item xs={12} mt={2}>
                  <FormControl component="fieldset">
                    <InputLabel shrink={true}>Ativo</InputLabel>
                    <Switch
                      {...register("is_enabled")}
                      checked={isEnabled}
                      onChange={handleSwitchChange}
                      color="primary"
                      name="is_enabled"
                    />
                  </FormControl>
                </Grid>
              ) : null}
              {showMarketDialog && (
          <Dialog open={showMarketDialog} onClose={() => setShowMarketDialog(false)}>
            <DialogTitle>Aviso</DialogTitle>
            <DialogContent>
              <DialogContentText>A escolha da loja só é necessária caso a chave do IO esteja atrelada apenas a uma loja e não a toda a marca.</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowMarketDialog(false)} color="primary">
                OK
              </Button>
            </DialogActions>
          </Dialog>
        )}
          </Grid>         
        </form>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setOpen(false)} color="error" variant="contained">Cancelar</Button>
      <LoadingButton loading={isSubmitting} form="offer-form" type="submit"
        variant="contained">Salvar</LoadingButton>
    </DialogActions>
  </Dialog>
}

export default withShowAlert(FormDialog)